import React from 'react';
import { getDisplayName, getDisplayInitials } from '../../lib/displayName';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Users, Dumbbell, Apple, Flame, ChevronRight, Trash2, CalendarCheck, Pill, Edit, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import InviteSection from './InviteSection';

import TagChip from '../TagChip';
import ClientTagAssignPopover from '../ClientTagAssignPopover';

// Shared by the flat and the grouped view so the two never drift apart.
const CARD_GRID = 'grid gap-4 sm:grid-cols-2 xl:grid-cols-3 items-start';

function ClientList({ clients, totalCount, pendingCounts = {}, onSelect, onRoutine, onEditRoutine, onDiet, onSupplements, onRemove, onCheckIn, tags = [], onTagsChanged, onTagCreated, groupBy = false, onPin }) {
  const { t } = useTranslation();

  const renderCard = (client) => (
        <Card key={client.id} className="border-border/50 hover:border-primary/50 transition-colors" data-testid={`client-card-${client.id}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => onSelect(client)}>
              <Avatar className="h-12 w-12 border-2 border-border shrink-0">
                {client.avatar_thumb ? (
                  <AvatarImage src={client.avatar_thumb} alt={client.name} />
                ) : null}
                <AvatarFallback className="bg-primary/10 text-primary font-bold">
                  {getDisplayInitials(client.name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-semibold truncate">{getDisplayName(client.name)}</p>
                  <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/30 shrink-0">{t('clients.clientBadge')}</Badge>
                  {pendingCounts[client.id] > 0 && (
                    <span className="shrink-0 h-5 min-w-[20px] px-1 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center" data-testid={`pending-badge-${client.id}`}>
                      {pendingCounts[client.id]}
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground truncate">{client.email}</p>
                <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1 truncate">
                    <Dumbbell className="h-3 w-3 text-primary shrink-0" />
                    <span className="truncate">{client.routine_name || t('clients.noRoutine')}</span>
                  </span>
                  <span className="flex items-center gap-1 shrink-0">
                    <Flame className="h-3 w-3 text-green-500" />
                    {client.macros_customized ? `${client.target_calories} kcal` : t('clients.noDiet', 'No diet')}
                  </span>
                </div>
              </div>
              <button type="button" onClick={(e) => { e.stopPropagation(); onPin(client.id, !client.pinned); }}
            className="shrink-0 text-muted-foreground hover:text-primary" data-testid={`pin-${client.id}`}
            aria-label={client.pinned ? t('clientTags.unpin', 'Unpin') : t('clientTags.pin', 'Pin')}>
            <Star className={`h-4 w-4 ${client.pinned ? 'fill-primary text-primary' : ''}`} />
          </button>
          <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0 hidden sm:block" />
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-border/30">
              {client.routine_id ? (
                <Button size="sm" className="flex-1 sm:flex-none bg-primary hover:bg-primary/90 text-white" onClick={(e) => { e.stopPropagation(); onEditRoutine(client); }} data-testid={`edit-routine-${client.id}`}>
                  <Dumbbell className="mr-1.5 h-3.5 w-3.5" /> {t('clients.routineBtn')}
                </Button>
              ) : (
                <Button variant="outline" size="sm" className="flex-1 sm:flex-none" onClick={(e) => { e.stopPropagation(); onRoutine(client); }} data-testid={`assign-routine-${client.id}`}>
                  <Dumbbell className="mr-1.5 h-3.5 w-3.5" /> {t('clients.assign')}
                </Button>
              )}
              {client.macros_customized && client.coach_diet_active !== false ? (
                <Button size="sm" className="flex-1 sm:flex-none bg-primary hover:bg-primary/90 text-white" onClick={(e) => { e.stopPropagation(); onDiet(client); }} data-testid={`edit-diet-${client.id}`}>
                  <Apple className="mr-1.5 h-3.5 w-3.5" /> {t('diet.title')}
                </Button>
              ) : (
                <Button variant="outline" size="sm" className="flex-1 sm:flex-none" onClick={(e) => { e.stopPropagation(); onDiet(client); }} data-testid={`edit-diet-${client.id}`}>
                  <Apple className="mr-1.5 h-3.5 w-3.5" /> {t('diet.title')}
                </Button>
              )}
              {client.has_supplement_plan ? (
                <Button size="sm" className="flex-1 sm:flex-none bg-primary hover:bg-primary/90 text-white" onClick={(e) => { e.stopPropagation(); onSupplements(client); }} data-testid={`edit-supplements-${client.id}`}>
                  <Pill className="mr-1.5 h-3.5 w-3.5" /> {t('nav.supplements')}
                </Button>
              ) : (
                <Button variant="outline" size="sm" className="flex-1 sm:flex-none" onClick={(e) => { e.stopPropagation(); onSupplements(client); }} data-testid={`edit-supplements-${client.id}`}>
                  <Pill className="mr-1.5 h-3.5 w-3.5" /> {t('nav.supplements')}
                </Button>
              )}
              {client.check_in_schedule?.enabled ? (
                <Button size="sm" className="flex-1 sm:flex-none bg-primary hover:bg-primary/90 text-white" onClick={(e) => { e.stopPropagation(); onCheckIn(client); }} data-testid={`checkins-btn-${client.id}`}>
                  <CalendarCheck className="mr-1.5 h-3.5 w-3.5" /> {t('clients.checkInsBtn')}
                </Button>
              ) : (
                <Button variant="outline" size="sm" className="flex-1 sm:flex-none" onClick={(e) => { e.stopPropagation(); onCheckIn(client); }} data-testid={`checkins-btn-${client.id}`}>
                  <CalendarCheck className="mr-1.5 h-3.5 w-3.5" /> {t('clients.checkInsBtn')}
                </Button>
              )}
              <Button variant="ghost" size="sm" className="text-destructive shrink-0" onClick={(e) => { e.stopPropagation(); onRemove(client); }} data-testid={`remove-client-${client.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 mt-2" onClick={(e) => e.stopPropagation()}>
            {(client.tags || []).map(tg => <TagChip key={tg.id} tag={tg} />)}
            <ClientTagAssignPopover
              subjectId={client.id}
              assigned={(client.tags || []).map(tg => tg.id)}
              tags={tags}
              onChanged={onTagsChanged}
              onTagCreated={onTagCreated}
            />
          </div>
        </CardContent>
        </Card>
  );

  const renderGroup = (key, heading, list) => (
    <div key={key}>
      <div className="flex items-center gap-2 mb-2">
        {heading}
        <span className="text-xs text-muted-foreground">{list.length}</span>
        <div className="flex-1 h-px bg-border" />
      </div>
      <div className={CARD_GRID}>{list.map(renderCard)}</div>
    </div>
  );

  return (
    <div className="space-y-6 pb-20 md:pb-0" data-testid="my-clients-page">
      <div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          {t('clients.titlePrefix')} <span className="text-primary">{t('clients.titleSuffix')}</span>
        </h1>
        <p className="text-muted-foreground mt-1">
          {totalCount != null && clients.length !== totalCount
            ? `${clients.length} of ${totalCount} ${t('clients.clientsAssignedToYou')}`
            : `${clients.length} ${clients.length === 1 ? t('clients.clientAssignedToYou', 'client assigned to you') : t('clients.clientsAssignedToYou')}`}
        </p>
      </div>

      {/* Invite Section */}
      <InviteSection />

      {clients.length === 0 ? (
        <Card className="border-border/50">
          <CardContent className="py-12 text-center">
            <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            {/* Distinguish "you have no clients" from "your filters match none" —
                otherwise a coach with 20 clients is told to send an invite. */}
            {totalCount > 0 ? (
              <>
                <h3 className="text-lg font-semibold mb-2">{t('clients.noMatches', 'No clients match')}</h3>
                <p className="text-muted-foreground">{t('clients.noMatchesHint', 'Try clearing your search, filter or tags.')}</p>
              </>
            ) : (
              <>
                <h3 className="text-lg font-semibold mb-2">{t('clients.noClientsYet')}</h3>
                <p className="text-muted-foreground">{t('clients.sendInviteHint')}</p>
              </>
            )}
          </CardContent>
        </Card>
      ) : (        groupBy ? (
          <div className="space-y-6">
            {tags.map(tg => {
              const inTag = clients.filter(c => (c.tags || []).some(x => x.id === tg.id));
              return inTag.length === 0 ? null : renderGroup(tg.id, <TagChip tag={tg} />, inTag);
            })}
            {(() => {
              const untagged = clients.filter(c => !(c.tags || []).length);
              return untagged.length === 0 ? null : renderGroup(
                '__untagged__',
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
                  {t('clientTags.untagged', 'Untagged')}
                </span>,
                untagged
              );
            })()}
          </div>
        ) : (
          <div className={CARD_GRID}>{clients.map(renderCard)}</div>
        )
      )}
    </div>
  );
}

export default ClientList;
